import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { canonicalHref, formatRoute, parseRoute, type Route } from '@visious/core';
import { preservedScrollState, scrollStateForPush, settleAfterPush } from './scroll';

/**
 * The web half of §8's URL scheme: `history`, `popstate` and the back stack. The *meaning* of a URL
 * is `parseRoute` in core, so Android's deep-link handler reads the same table from the same code
 * rather than from a second implementation of the same prose.
 *
 * Hand-rolled rather than React Router, for three reasons that all point the same way:
 *
 * 1. §8's route table is already a core function. A router library's job is to own that table, so
 *    we would be paying for a matcher we are contractually not allowed to use.
 * 2. What is left is ~50 lines of `pushState` + `popstate`, and `useSyncExternalStore` is precisely
 *    the hook for it — no tearing under concurrent rendering, no subscription bookkeeping.
 * 3. Every route-shaped decision stays portable. Nothing here is imported by a screen model.
 *
 * Scroll behaviour lives next door in `scroll.ts`: a push to a different screen reads from the top,
 * Back/Forward restore the position the entry was left at, and what "different screen" means is
 * core's `screenIdentity`. This file only threads the entry ids through `pushState`/`replaceState`.
 */

/** `pushState` does not fire `popstate`, so navigation inside the app needs its own notification. */
const NAVIGATE_EVENT = 'visious:navigate';

/**
 * The address bar as this app spells it: path and query, never the origin or the hash.
 *
 * Exported for the one store that keeps a fact about *this page* rather than about the app —
 * `tabs/store.ts`, which has to be able to tell "the page that gave its videos away" from the next
 * page the reader opens in the same tab.
 */
export function currentHref(): string {
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

function subscribe(onChange: () => void): () => void {
  globalThis.addEventListener('popstate', onChange);
  globalThis.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    globalThis.removeEventListener('popstate', onChange);
    globalThis.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

export interface NavigateOptions {
  /** Replace the current entry instead of pushing one — for canonicalising a URL in place. */
  readonly replace?: boolean;
}

/**
 * Go to an in-app href. Same-href navigation is dropped rather than pushed: a second click on the
 * link you are already on should not cost a back-button press to undo.
 */
export function navigate(href: string, options: NavigateOptions = {}): void {
  if (href === currentHref()) return;
  if (options.replace === true) {
    // The entry's scroll id survives a rewrite: canonicalising a spelling must not lose the place.
    globalThis.history.replaceState(preservedScrollState(), '', href);
    globalThis.dispatchEvent(new Event(NAVIGATE_EVENT));
    return;
  }
  const from = currentHref();
  globalThis.history.pushState(scrollStateForPush(), '', href);
  globalThis.dispatchEvent(new Event(NAVIGATE_EVENT));
  settleAfterPush(from, href);
}

/** The same thing, from a {@link Route} — so a caller never hand-builds a URL string. */
export function navigateTo(route: Route, options: NavigateOptions = {}): void {
  navigate(formatRoute(route), options);
}

/**
 * Every way the address bar can change, as one subscription — for a store outside React that has to
 * notice a navigation. A component does not need it: `useRoute` and `useHref` below are the same
 * subscription with a snapshot attached.
 */
export function subscribeHref(onChange: () => void): () => void {
  return subscribe(onChange);
}

/** The current route, re-read whenever the address bar changes for any reason. */
export function useRoute(): Route {
  const href = useSyncExternalStore(subscribe, currentHref, () => '/');
  return parseRoute(href);
}

/**
 * The address bar as text, for the one consumer that wants the *change* rather than the meaning:
 * `ScreenBoundary` clears a caught error when the reader navigates, and "did the reader navigate" is a
 * question about the string. Deriving it from `useRoute` instead would miss a move between two URLs
 * that parse to the same route — `/watch?v=X&t=90` and `/watch?v=X` — which is exactly the kind of
 * navigation somebody makes to get away from a page that has just broken.
 */
export function useHref(): string {
  return useSyncExternalStore(subscribe, currentHref, () => '/');
}

/**
 * §8's self-redirect requirement, as one effect: if the address bar spells a route non-canonically,
 * rewrite it in place. `/shorts/ID` becomes `/watch?v=ID`, `/feed/library` becomes `/feed/playlists`,
 * `/search?q=` becomes `/results?search_query=`, a trailing slash and a `?si=` tracking parameter go.
 *
 * Three properties make this safe to run on every render, and all three belong to `canonicalHref`
 * rather than to this hook: it answers `null` for a path it could not read (so a 404 keeps its
 * evidence), `null` for the three arms whose screen does a lookup (so a resolver is not rewritten out
 * from under itself), and its output is a **fixed point** — which is what stops a replace-then-re-read
 * cycle. The testkit asserts all three.
 *
 * `replaceState`, never a push: the reader never asked to visit the non-canonical spelling, so it must
 * not cost a Back press to leave.
 */
export function useCanonicalUrl(): void {
  const href = useSyncExternalStore(subscribe, currentHref, () => '/');
  const canonical = canonicalHref(href);
  useEffect(() => {
    if (canonical !== null) navigate(canonical, { replace: true });
  }, [canonical]);
}

/**
 * `navigate` bound for a component: `.tsx` files may not import core (R13), and this keeps the
 * anchor-click handling in one place rather than in every link.
 */
export function useNavigate(): (href: string, options?: NavigateOptions) => void {
  return useCallback((href: string, options?: NavigateOptions) => navigate(href, options), []);
}

export type { Route };
